import api from '../../api/client';
import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  Row,
  Col,
  Tabs,
  Statistic,
  Progress,
  Table,
  Tag,
  Badge,
  Alert,
  Spin,
  message,
  Space,
  Tooltip,
  Button,
  Input,
  Select,
  Modal,
  Descriptions,
  Divider,
  Timeline,
  Typography,
  Empty,
  ConfigProvider
} from "antd";
import {
  WarningOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined,
  EyeOutlined,
  TeamOutlined,
  DropboxOutlined,
  CalendarOutlined,
  HeartOutlined,
  RiseOutlined
} from "@ant-design/icons";
import { ProTable } from "@ant-design/pro-table";
import axios from "axios";
import dayjs from "dayjs";
import config from "../../config";
import enUS from 'antd/lib/locale/en_US';


const { Title, Text } = Typography;
const { TabPane } = Tabs;

const AnimalCyclePage = ({ species }) => {
  const [loading, setLoading] = useState(false);
  const [animals, setAnimals] = useState([]);
  const [youngAnimals, setYoungAnimals] = useState([]);
  const [birthCohorts, setBirthCohorts] = useState([]);
  const [metrics, setMetrics] = useState({
    totalSired: 0,
    totalYoung: 0,
    totalMortality: 0,
    totalSold: 0,
    youngMortality: 0,
    youngSold: 0,
    avgWeight: 0
  });
  const [stageDistribution, setStageDistribution] = useState({
    Newborn: 0,
    Weaner: 0,
    Grower: 0,
    Adult: 0
  });
  const [youngStageDistribution, setYoungStageDistribution] = useState({
    Newborn: 0,
    Weaner: 0,
    Grower: 0
  });
  const [insights, setInsights] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [selectedAnimal, setSelectedAnimal] = useState(null);
  const [detailsModal, setDetailsModal] = useState(false);
  const [searchText, setSearchText] = useState("");
  const actionRef = useRef();

  const environment = window.location.hostname === "localhost" ? "development" : "production";
  const apiUrl = config[environment].apiUrl;

  // Fetch data from API
  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/animal-cycles/${species}`);
      const data = response.data;
      
      setAnimals(data.animals || []);
      setYoungAnimals(data.youngAnimals || []);
      setBirthCohorts(data.birthCohorts || []);
      setMetrics(data.metrics || {});
      setStageDistribution(data.stageDistribution || {});
      setYoungStageDistribution(data.youngStageDistribution || {});
      setUpcomingEvents(data.upcomingEvents || []);
      
      generateInsights(data.metrics, data.stageDistribution, data.youngStageDistribution);
    } catch (error) {
      console.error("Error fetching data:", error);
      message.error("Failed to load animal data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [species]);

  // Generate insights based on data
  const generateInsights = (metrics, stageDist, youngDist) => {
    const newInsights = [];
    const youngName = species === "goat" ? "kids" : species === "sheep" ? "lambs" : "piglets";

    if (metrics.totalSired > 0) {
      const mortalityRate = (metrics.totalMortality / metrics.totalSired) * 100;
      newInsights.push({
        type: mortalityRate > 15 ? "error" : mortalityRate > 8 ? "warning" : "success",
        icon: mortalityRate > 15 ? <WarningOutlined /> : <CheckCircleOutlined />,
        title: "Offspring Survival Rate",
        message: `${metrics.totalSired} ${youngName} born with ${metrics.totalMortality} mortalities (${mortalityRate.toFixed(1)}%). ${
          mortalityRate > 15 ? "High mortality rate needs attention!" : 
          mortalityRate > 8 ? "Monitor closely." : "Good survival rate!"
        }`,
      });
    }

    if (metrics.youngMortality > 0) {
      const youngMortalityRate = (metrics.youngMortality / (metrics.totalYoung || 1)) * 100;
      newInsights.push({
        type: youngMortalityRate > 10 ? "warning" : "info",
        icon: youngMortalityRate > 10 ? <WarningOutlined /> : <InfoCircleOutlined />,
        title: "Young Animal Health",
        message: `${metrics.youngMortality} young ${youngName} deaths recorded. ${
          youngMortalityRate > 10 ? "Consider veterinary review." : "Within acceptable range."
        }`,
      });
    }

    if (metrics.avgWeight > 0) {
      newInsights.push({
        type: "info",
        icon: <InfoCircleOutlined />,
        title: "Average Weight",
        message: `Average weight of young ${youngName} is ${metrics.avgWeight} kg. Monitor growth rates.`,
      });
    }

    setInsights(newInsights);
  };

  // Parent animals table columns
  const parentColumns = [
    {
      title: "Tag Number",
      dataIndex: "tagNumber",
      key: "tagNumber",
      width: 120,
      fixed: "left",
      render: (text, record) => (
        <Button type="link" onClick={() => showAnimalDetails(record)} style={{ padding: 0 }}>
          <Text>{text}</Text>
        </Button>
      ),
    },
    {
      title: "Date of Birth",
      dataIndex: "dob",
      key: "dob",
      width: 110,
      render: (date) => date ? dayjs(date).format("DD MMM YYYY") : "-",
      sorter: (a, b) => dayjs(a.dob).unix() - dayjs(b.dob).unix(),
    },
    {
      title: "Age (months)",
      dataIndex: "ageMonths",
      key: "ageMonths",
      width: 100,
      sorter: (a, b) => (a.ageMonths || 0) - (b.ageMonths || 0),
    },
    {
      title: "Stage",
      dataIndex: "stage",
      key: "stage",
      width: 110,
      render: (stage) => {
        const colors = { Newborn: "blue", Weaner: "cyan", Grower: "orange", Adult: "green" };
        return <Tag color={colors[stage] || "default"}>{stage}</Tag>;
      },
    },
    {
      title: "Farm",
      dataIndex: "farm",
      key: "farm",
      width: 100,
    },
    {
      title: "Offspring",
      dataIndex: "totalSired",
      key: "totalSired",
      width: 120,
      render: (total, record) => (
        <Space>
          <Text strong>{total || 0}</Text>
          {record.offspringInQueue > 0 && (
            <Tag color="orange">{record.offspringInQueue} in queue</Tag>
          )}
        </Space>
      ),
    },
    {
      title: "Last Birth",
      dataIndex: "lastBorn",
      key: "lastBorn",
      width: 110,
      render: (date) => date ? dayjs(date).format("DD MMM YYYY") : "-",
    },
    {
      title: "Value",
      dataIndex: "currentValue",
      key: "currentValue",
      width: 100,
      render: (value) => value ? `KES ${value.toLocaleString()}` : "-",
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      fixed: "right",
      render: (_, record) => (
        <Tooltip title="View Details">
          <Button icon={<EyeOutlined />} size="small" onClick={() => showAnimalDetails(record)} />
        </Tooltip>
      ),
    },
  ];

  // Young animals table columns
  const youngColumns = [
    {
      title: "Tag Number",
      dataIndex: "tagNumber",
      key: "tagNumber",
      width: 120,
      fixed: "left",
      render: (text, record) => (
        <Button type="link" onClick={() => showAnimalDetails(record)} style={{ padding: 0 }}>
          <Text>{text}</Text>
        </Button>
      ),
    },
    {
      title: "Date of Birth",
      dataIndex: "dob",
      key: "dob",
      width: 110,
      render: (date) => date ? dayjs(date).format("DD MMM YYYY") : "-",
      sorter: (a, b) => dayjs(a.dob).unix() - dayjs(b.dob).unix(),
    },
    {
      title: "Age (days)",
      dataIndex: "ageDays",
      key: "ageDays",
      width: 100,
      sorter: (a, b) => (a.ageDays || 0) - (b.ageDays || 0),
    },
    {
      title: "Stage",
      dataIndex: "stage",
      key: "stage",
      width: 100,
      render: (stage) => {
        const colors = { Newborn: "blue", Weaner: "cyan", Grower: "orange" };
        return <Tag color={colors[stage] || "default"}>{stage}</Tag>;
      },
    },
    {
      title: "Weight (kg)",
      dataIndex: "weight",
      key: "weight",
      width: 100,
      sorter: (a, b) => (a.weight || 0) - (b.weight || 0),
    },
    {
      title: "Parent",
      dataIndex: "parentTag",
      key: "parentTag",
      width: 120,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (status) => (
        <Tag color={status === "Active" ? "green" : status === "mortality" ? "red" : "orange"}>
          {status || "Active"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      fixed: "right",
      render: (_, record) => (
        <Tooltip title="View Details">
          <Button icon={<EyeOutlined />} size="small" onClick={() => showAnimalDetails(record)} />
        </Tooltip>
      ),
    },
  ];

  // Birth cohorts columns
  const cohortColumns = [
    {
      title: "Birth Date",
      dataIndex: "birthDate",
      key: "birthDate",
      width: 120,
      render: (date) => dayjs(date).format("DD MMM YYYY"),
    },
    {
      title: "Count",
      dataIndex: "count",
      key: "count",
      width: 80,
      render: (count) => <Badge count={count} showZero color="blue" />,
    },
    {
      title: "Stage Distribution",
      dataIndex: "stageDistribution",
      key: "stageDistribution",
      render: (stages) => (
        <Space>
          {Object.entries(stages || {}).map(([stage, count]) => (
            <Tag key={stage} color="blue">
              {stage}: {count}
            </Tag>
          ))}
        </Space>
      ),
    },
    {
      title: "Parents",
      dataIndex: "parents",
      key: "parents",
      render: (parents) => (
        <Space wrap>
          {parents?.slice(0, 3).map((parent, idx) => (
            <Tag key={idx} color="purple">
              {parent}
            </Tag>
          ))}
          {parents?.length > 3 && <Text type="secondary">+{parents.length - 3} more</Text>}
        </Space>
      ),
    },
    {
      title: "Insights",
      dataIndex: "insights",
      key: "insights",
      render: (insight) => (
        <Tooltip title={insight?.message}>
          <Tag color={insight?.priority === "high" ? "red" : "orange"}>
            {insight?.title}
          </Tag>
        </Tooltip>
      ),
    },
  ];

  // Upcoming events columns
  const eventsColumns = [
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      width: 110,
      render: (date) => dayjs(date).format("DD MMM YYYY"),
      sorter: (a, b) => dayjs(a.date).unix() - dayjs(b.date).unix(),
    },
    {
      title: "Days Until",
      dataIndex: "daysUntil",
      key: "daysUntil",
      width: 90,
      render: (days) => (
        <Badge 
          count={days} 
          style={{ 
            backgroundColor: days <= 7 ? "#ff4d4f" : days <= 14 ? "#faad14" : "#1890ff",
            borderRadius: 12,
            fontSize: 11
          }} 
        />
      ),
    },
    {
      title: "Animal",
      dataIndex: "animalTag",
      key: "animalTag",
      width: 120,
    },
    {
      title: "Parent",
      dataIndex: "parentTag",
      key: "parentTag",
      width: 120,
    },
    {
      title: "Farm",
      dataIndex: "farm",
      key: "farm",
      width: 100,
    },
    {
      title: "Event",
      dataIndex: "event",
      key: "event",
      ellipsis: true,
    },
    {
      title: "Priority",
      dataIndex: "priority",
      key: "priority",
      width: 100,
      render: (priority) => (
        <Tag color={priority === "high" ? "red" : priority === "medium" ? "orange" : "blue"}>
          {priority?.toUpperCase()}
        </Tag>
      ),
    },
  ];

  const showAnimalDetails = async (record) => {
    try {
      setSelectedAnimal(record);
      setDetailsModal(true);
    } catch (error) {
      console.error("Error fetching animal details:", error);
      message.error("Failed to load animal details");
    }
  };

  // Filter animals based on search
  const filteredAnimals = animals.filter(animal => 
    searchText ? animal.tagNumber?.toLowerCase().includes(searchText.toLowerCase()) : true
  );

  const totalAnimals = Object.values(stageDistribution).reduce((sum, count) => sum + count, 0);
  const totalYoung = Object.values(youngStageDistribution).reduce((sum, count) => sum + count, 0);
  const conceptionRate = metrics.totalSired > 0 
    ? ((metrics.totalYoung / metrics.totalSired) * 100).toFixed(1) 
    : 0;

  const speciesDisplay = {
    goat: { name: "Goats", young: "Kids", icon: "🐐", color: "#52c41a" },
    sheep: { name: "Sheep", young: "Lambs", icon: "🐑", color: "#1890ff" },
    pig: { name: "Pigs", young: "Piglets", icon: "🐷", color: "#fa8c16" },
  }[species] || { name: species, young: "Young", icon: "🐾", color: "#722ed1" };

  if (loading && animals.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: 50 }}>
        <Spin size="large" tip="Loading animal data..." />
      </div>
    );
  }

  return (
    <>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Title level={3}>
          <span style={{ fontSize: 32, marginRight: 8 }}>{speciesDisplay.icon}</span>
          {speciesDisplay.name} Lifecycle Management
        </Title>
        <Text type="secondary">Track and manage the complete lifecycle of your {speciesDisplay.name.toLowerCase()}</Text>
      </div>

      {/* Insights Alerts */}
      {insights.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          {insights.map((insight, index) => (
            <Alert
              key={index}
              message={insight.title}
              description={insight.message}
              type={insight.type}
              icon={insight.icon}
              showIcon
              style={{ marginBottom: 8 }}
              closable
            />
          ))}
        </div>
      )}

      {/* Metrics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card >
            <Statistic
              title="Total Sired"
              value={metrics.totalSired || 0}
              valueStyle={{ color: "#3f8600", fontSize: 28 }}
              prefix={<HeartOutlined />}
            />
            <div style={{ marginTop: 8, fontSize: 12, color: "#666" }}>
              {metrics.totalMortality || 0} mortalities
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={`${speciesDisplay.young} Born`}
              value={metrics.totalYoung || 0}
              valueStyle={{ color: "#1890ff", fontSize: 28 }}
              prefix={<DropboxOutlined />}
            />
            <div style={{ marginTop: 8, fontSize: 12, color: "#666" }}>
              Avg weight: {metrics.avgWeight || 0} kg
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Mortality"
              value={metrics.youngMortality || 0}
              valueStyle={{ color: "#cf1322", fontSize: 28 }}
              prefix={<WarningOutlined />}
            />
            <div style={{ marginTop: 8, fontSize: 12, color: "#666" }}>
              Conception rate: {conceptionRate}%
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Adult Animals"
              value={totalAnimals}
              valueStyle={{ color: "#722ed1", fontSize: 28 }}
              prefix={<TeamOutlined />}
            />
            <div style={{ marginTop: 8, fontSize: 12, color: "#666" }}>
              {stageDistribution.Adult || 0} breeding adults
            </div>
          </Card>
        </Col>
      </Row>

      {/* Stage Distribution Progress */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={12}>
          <Card size="small" title="Parent Stage Distribution">
            {Object.entries(stageDistribution).map(([stage, count]) => {
              const percent = totalAnimals > 0 ? (count / totalAnimals) * 100 : 0;
              const colors = { Newborn: "#1890ff", Weaner: "#13c2c2", Grower: "#fa8c16", Adult: "#52c41a" };
              return (
                <div key={stage} style={{ marginBottom: 16 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                    <span>
                      <Tag color={colors[stage]}>{stage}</Tag>
                      <Text>{count} animals</Text>
                    </span>
                    <Text type="secondary">{percent.toFixed(1)}%</Text>
                  </div>
                  <Progress percent={percent} strokeColor={colors[stage]} showInfo={false} size="small" />
                </div>
              );
            })}
          </Card>
        </Col>
        <Col span={12}>
          <Card size="small" title={`${speciesDisplay.young} Stage Distribution`}>
            {Object.entries(youngStageDistribution).map(([stage, count]) => {
              const percent = totalYoung > 0 ? (count / totalYoung) * 100 : 0;
              const colors = { Newborn: "#1890ff", Weaner: "#13c2c2", Grower: "#fa8c16" };
              return (
                <div key={stage} style={{ marginBottom: 16 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                    <span>
                      <Tag color={colors[stage]}>{stage}</Tag>
                      <Text>{count} animals</Text>
                    </span>
                    <Text type="secondary">{percent.toFixed(1)}%</Text>
                  </div>
                  <Progress percent={percent} strokeColor={colors[stage]} showInfo={false} size="small" />
                </div>
              );
            })}
          </Card>
        </Col>
      </Row>

      {/* Tabs for different views */}
      <Card>
        <Tabs defaultActiveKey="parents" type="card">
          <TabPane tab={`Parent Animals (${animals.length})`} key="parents">
            <div style={{ marginBottom: 16 }}>
              <Input.Search
                placeholder="Search by tag number..."
                allowClear
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{ width: 300 }}
                onSearch={() => {}}
              />
            </div>
            <ProTable
              columns={parentColumns}
              dataSource={filteredAnimals}
              rowKey="id"
              loading={loading}
              search={false}
              pagination={{ 
                pageSize: 10, 
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} animals`
              }}
              toolBarRender={false}
              scroll={{ x: 1000 }}
              options={false}
            />
          </TabPane>

          <TabPane tab={`${speciesDisplay.young} (${youngAnimals.length})`} key="young">
            <ProTable
              columns={youngColumns}
              dataSource={youngAnimals}
              rowKey="id"
              loading={loading}
              search={false}
              pagination={{ pageSize: 10, showSizeChanger: true }}
              toolBarRender={false}
              scroll={{ x: 1000 }}
              options={false}
              expandable={{
                expandedRowRender: (record) => (
                  <div style={{ padding: 16, background: "#fafafa", borderRadius: 8 }}>
                    <Row gutter={16}>
                      <Col span={12}>
                        <Card size="small" title="Age-Based Insights" style={{ marginBottom: 0 }}>
                          {record.ageInsights ? (
                            <>
                              <Alert
                                message={record.ageInsights.title}
                                description={record.ageInsights.message}
                                type={record.ageInsights.priority === "high" ? "warning" : "info"}
                                showIcon
                              />
                              <div style={{ marginTop: 12 }}>
                                <Text strong>Recommended Actions:</Text>
                                <ul style={{ marginTop: 8, marginBottom: 0 }}>
                                  {record.ageInsights.actions?.map((action, idx) => (
                                    <li key={idx}>{action}</li>
                                  ))}
                                </ul>
                              </div>
                            </>
                          ) : (
                            <Text type="secondary">No insights available</Text>
                          )}
                        </Card>
                      </Col>
                      <Col span={12}>
                        <Card size="small" title="Upcoming Events" style={{ marginBottom: 0 }}>
                          {record.upcomingEvents?.length > 0 ? (
                            <Timeline style={{ marginTop: 8 }}>
                              {record.upcomingEvents.map((event, idx) => (
                                <Timeline.Item key={idx} color={event.priority === "high" ? "red" : "blue"}>
                                  <div>
                                    <Text strong>{event.event}</Text>
                                    <br />
                                    <Text type="secondary">{dayjs(event.date).format("DD MMM YYYY")}</Text>
                                    <Tag color={event.priority === "high" ? "red" : "orange"} style={{ marginLeft: 8 }}>
                                      {event.priority}
                                    </Tag>
                                  </div>
                                </Timeline.Item>
                              ))}
                            </Timeline>
                          ) : (
                            <Text type="secondary">No upcoming events</Text>
                          )}
                        </Card>
                      </Col>
                    </Row>
                  </div>
                ),
              }}
            />
          </TabPane>

          <TabPane tab={`Birth Cohorts (${birthCohorts.length})`} key="cohorts">
            <ProTable
              columns={cohortColumns}
              dataSource={birthCohorts}
              rowKey="birthDate"
              loading={loading}
              search={false}
              pagination={{ pageSize: 5, showSizeChanger: true }}
              toolBarRender={false}
              options={false}
            />
          </TabPane>

          <TabPane tab={`Upcoming Events (${upcomingEvents.length})`} key="events">
            {upcomingEvents.length > 0 ? (
              <ProTable
                columns={eventsColumns}
                dataSource={upcomingEvents}
                rowKey="date"
                loading={loading}
                search={false}
                pagination={{ pageSize: 10, showSizeChanger: true }}
                toolBarRender={false}
                scroll={{ x: 1000 }}
                options={false}
              />
            ) : (
              <Empty description="No upcoming events" />
            )}
          </TabPane>
        </Tabs>
      </Card>

      {/* Animal Details Modal */}
      <Modal
        title={`Animal Details: ${selectedAnimal?.tagNumber || selectedAnimal?.id}`}
        open={detailsModal}
        onCancel={() => setDetailsModal(false)}
        footer={[
          <Button key="close" onClick={() => setDetailsModal(false)}>
            Close
          </Button>,
        ]}
        width={700}
      >
        {selectedAnimal && (
          <>
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="Tag Number">
                <Tag color="blue">{selectedAnimal.tagNumber || "-"}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Species">
                <Tag color={speciesDisplay.color}>{speciesDisplay.name}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Date of Birth">
                {selectedAnimal.dob ? dayjs(selectedAnimal.dob).format("DD MMM YYYY") : "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Age">
                {selectedAnimal.ageMonths ? `${selectedAnimal.ageMonths} months` : 
                 selectedAnimal.ageDays ? `${selectedAnimal.ageDays} days` : "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Stage">
                <Tag color="blue">{selectedAnimal.stage}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Farm">{selectedAnimal.farm || "-"}</Descriptions.Item>
              <Descriptions.Item label="Gender">{selectedAnimal.gender || "-"}</Descriptions.Item>
              <Descriptions.Item label="Breed">{selectedAnimal.breed || "-"}</Descriptions.Item>
              <Descriptions.Item label="Weight">{selectedAnimal.weight ? `${selectedAnimal.weight} kg` : "-"}</Descriptions.Item>
              <Descriptions.Item label="Status" span={2}>
                <Tag color={selectedAnimal.status === "Active" ? "green" : "orange"}>{selectedAnimal.status}</Tag>
              </Descriptions.Item>
              {selectedAnimal.parentTag && (
                <Descriptions.Item label="Parent Tag" span={2}>
                  <Tag color="purple">{selectedAnimal.parentTag}</Tag>
                </Descriptions.Item>
              )}
              {selectedAnimal.totalSired > 0 && (
                <Descriptions.Item label="Total Offspring" span={2}>
                  <Tag color="green">{selectedAnimal.totalSired} offspring</Tag>
                </Descriptions.Item>
              )}
            </Descriptions>
          </>
        )}
      </Modal>
    </>
  );
};

export default AnimalCyclePage;